import React, { useRef, useEffect, useState, ClipboardEvent, KeyboardEvent } from "react";
import { Box, TextField, styled } from "@mui/material";

interface OtpInputProps {
    length?: number;
    value: string;
    onChange: (value: string) => void;
    onComplete?: (value: string) => void;
    error?: boolean;
}

const StyledInput = styled(TextField)<{ error?: boolean }>(({ theme, error }) => ({
    "& .MuiInputBase-root": {
        height: 56,
        width: 56,
        borderRadius: 12,
        fontSize: "1.5rem",
        fontWeight: "bold",
        textAlign: "center",
        backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "#fff",
        border: `2px solid ${error ? theme.palette.error.main : theme.palette.divider}`,
        transition: "all 0.2s ease-in-out",
        "&.Mui-focused": {
            borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
            boxShadow: `0 0 0 4px ${error ? "rgba(211, 47, 47, 0.1)" : "rgba(25, 118, 210, 0.1)"}`,
            transform: "translateY(-2px)",
        },
        "&:hover": {
            borderColor: error ? theme.palette.error.dark : theme.palette.primary.light,
        },
    },
    "& .MuiInputBase-input": {
        textAlign: "center",
        padding: 0,
    },
    "& fieldset": {
        border: "none",
    },
}));

const OtpInput: React.FC<OtpInputProps> = ({
    length = 4,
    value,
    onChange,
    onComplete,
    error,
}) => {
    const [digits, setDigits] = useState<string[]>(Array(length).fill(""));
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
        const newDigits = value.split("").slice(0, length);
        // Fill the rest with empty strings if value is shorter than length
        while (newDigits.length < length) {
            newDigits.push("");
        }
        setDigits(newDigits);
    }, [value, length]);

    const handleChange = (index: number, val: string) => {
        if (!/^\d*$/.test(val)) return;

        const newDigits = [...digits];
        // Take the last character if multiple are typed (though maxLength is 1)
        newDigits[index] = val.slice(-1);

        const newValue = newDigits.join("");
        onChange(newValue);
        setDigits(newDigits);

        if (val && index < length - 1) {
            inputRefs.current[index + 1]?.focus();
        }

        if (newValue.length === length && onComplete) {
            onComplete(newValue);
        }
    };

    const handleKeyDown = (index: number, e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Backspace") {
            if (!digits[index] && index > 0) {
                // If current is empty, move back and delete previous
                const newDigits = [...digits];
                newDigits[index - 1] = "";
                setDigits(newDigits);
                onChange(newDigits.join(""));
                inputRefs.current[index - 1]?.focus();
            } else {
                // Just clear current
                const newDigits = [...digits];
                newDigits[index] = "";
                setDigits(newDigits);
                onChange(newDigits.join(""));
            }
        } else if (e.key === "ArrowLeft" && index > 0) {
            inputRefs.current[index - 1]?.focus();
        } else if (e.key === "ArrowRight" && index < length - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
        if (pastedData) {
            onChange(pastedData);
            const newDigits = pastedData.split("");
            while (newDigits.length < length) newDigits.push("");
            setDigits(newDigits);

            // Focus the input after the last pasted character or the last input
            const nextFocusIndex = Math.min(pastedData.length, length - 1);
            inputRefs.current[nextFocusIndex]?.focus();

            if (pastedData.length === length && onComplete) {
                onComplete(pastedData);
            }
        }
    };

    return (
        <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", my: 2 }}>
            {Array.from({ length }).map((_, index) => (
                <StyledInput
                    key={index}
                    error={error}
                    value={digits[index]}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    inputRef={(el) => (inputRefs.current[index] = el)}
                    inputProps={{
                        maxLength: 1,
                        inputMode: "numeric",
                        "aria-label": `Digit ${index + 1}`,
                    }}
                />
            ))}
        </Box>
    );
};

export default OtpInput;
